import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { rolesApi } from "@/api/users";
import { Badge, Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { MODULE_GUIDE, PERMISSION_GUIDE } from "./rolePermissionGuide";

export function RolesPage() {
  const { hasPermission } = useAuth();
  const dialogs = useDialogs();
  const canEdit = hasPermission("System.Configure");

  const qc = useQueryClient();
  const matrix = useQuery({
    queryKey: ["role-matrix"],
    queryFn: () => rolesApi.matrix(),
  });

  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!matrix.data) return;
    const first = activeRole ?? matrix.data.roles[0]?.code ?? null;
    setActiveRole(first);
    if (first) {
      setDraft(new Set(matrix.data.grants[first] ?? []));
      setDirty(false);
    }
  }, [matrix.data, activeRole]);

  const save = useMutation({
    mutationFn: ({ roleCode, permissions }: { roleCode: string; permissions: string[] }) =>
      rolesApi.set(roleCode, permissions),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["role-matrix"] });
    },
  });

  const groupedPermissions = useMemo(() => {
    if (!matrix.data) return [] as Array<[string, string[]]>;
    const groups: Record<string, string[]> = {};
    for (const p of matrix.data.permissions) {
      const module = p.split(".")[0] ?? "Other";
      (groups[module] ??= []).push(p);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [matrix.data]);

  const toggle = (perm: string) => {
    if (!canEdit) return;
    setDraft((current) => {
      const next = new Set(current);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
    setDirty(true);
  };

  const reset = () => {
    if (!matrix.data || !activeRole) return;
    setDraft(new Set(matrix.data.grants[activeRole] ?? []));
    setDirty(false);
  };

  const changeRole = async (code: string) => {
    if (dirty) {
      const ok = await dialogs.confirm({
        title: "Discard changes?",
        message: "Discard unsaved permission changes?",
        confirmLabel: "Discard",
        destructive: true,
      });
      if (!ok) return;
    }
    setActiveRole(code);
    setDraft(new Set(matrix.data?.grants[code] ?? []));
    setDirty(false);
  };

  if (matrix.isLoading) {
    return (
      <>
        <PageHeader eyebrow="Admin" title="Roles & Permissions" />
        <div className="text-caption uppercase text-gray-mid">Loading matrix…</div>
      </>
    );
  }

  const data = matrix.data;
  if (!data || !activeRole) return null;

  const activeMeta = data.roles.find((r) => r.code === activeRole);

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Roles & Permissions"
        description="Pick a role code, toggle the Module.Action permissions it grants, then save. Every change is written to the audit log."
        actions={
          <div className="flex items-center gap-space-3">
            <Select value={activeRole} onChange={(e) => changeRole(e.target.value)} className="w-64">
              {data.roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} — {r.title}
                </option>
              ))}
            </Select>
            {canEdit && (
              <>
                <Button variant="secondary" onClick={reset} disabled={!dirty || save.isPending}>
                  Reset
                </Button>
                <Button
                  onClick={() => save.mutate({ roleCode: activeRole, permissions: [...draft] })}
                  loading={save.isPending}
                  disabled={!dirty}
                >
                  <Save size={16} aria-hidden /> Save {activeRole}
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-space-3 mb-space-4">
        <Badge status="gold">{activeMeta?.code}</Badge>
        <span className="text-small text-gray-mid">{activeMeta?.title}</span>
        <span className="text-caption text-gray-mid">
          {draft.size} / {data.permissions.length} permissions
        </span>
        {!canEdit && (
          <Badge status="archived">Read only — missing System.Configure</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-space-3">
        {groupedPermissions.map(([module, perms]) => {
          const allGranted = perms.every((p) => draft.has(p));
          const noneGranted = perms.every((p) => !draft.has(p));
          return (
            <div
              key={module}
              className="border border-border-light rounded-lg bg-white-pure p-space-3"
            >
              <div className="flex items-center justify-between mb-space-2">
                <h3 className="font-display font-bold text-small uppercase tracking-wider text-onyx">
                  {module}
                </h3>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setDraft((current) => {
                        const next = new Set(current);
                        if (allGranted) perms.forEach((p) => next.delete(p));
                        else perms.forEach((p) => next.add(p));
                        return next;
                      });
                      setDirty(true);
                    }}
                    className="text-caption text-gold-shadow hover:underline"
                  >
                    {allGranted ? "Clear" : noneGranted ? "Grant all" : "Grant remaining"}
                  </button>
                )}
              </div>
              <ul className="space-y-space-1">
                {perms.map((perm) => {
                  const checked = draft.has(perm);
                  return (
                    <li key={perm}>
                      <label className="flex items-center gap-space-2 text-small">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!canEdit}
                          onChange={() => toggle(perm)}
                          className="h-4 w-4 accent-gold"
                        />
                        <span className="font-mono">{perm}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <details className="mt-space-3 border-t border-border-light pt-space-2">
                <summary className="text-caption uppercase tracking-wider text-gold-shadow cursor-pointer hover:underline">
                  What does this control?
                </summary>
                <div className="mt-space-2 space-y-space-2">
                  {MODULE_GUIDE[module] && (
                    <p className="text-caption text-gray-mid">{MODULE_GUIDE[module]}</p>
                  )}
                  <dl className="space-y-space-2">
                    {perms.map((perm) => (
                      <div key={perm}>
                        <dt className="font-mono text-caption text-onyx">{perm}</dt>
                        <dd className="text-caption text-gray-mid">
                          {PERMISSION_GUIDE[perm] ?? "No description available yet."}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </details>
            </div>
          );
        })}
      </div>
    </>
  );
}
